import ImageIcon from '@mui/icons-material/Image'
import {
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Chip,
} from '@mui/material'
import React, { useState, useCallback, useEffect } from 'react'
import { FileError, FileRejection, useDropzone } from 'react-dropzone'
import { showErrorToast } from '../../services/CommonServices/alertServices'

type ImageDropzoneProps = {
	setFiles: (files: File[]) => void
	externalOpen?: boolean
	setExternalOpen?: (open: boolean) => void
	maxFiles?: number
	title?: string
	additionalFilesTypes?: string[]
}

const ImageDropzone = ({
	setFiles,
	externalOpen,
	setExternalOpen,
	maxFiles,
	title = 'Upload Images',
	additionalFilesTypes = [],
}: ImageDropzoneProps) => {
	const [open, setOpen] = useState(false)
	const [selectedFiles, setSelectedFiles] = useState<File[]>([])

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			selectedFiles.forEach((file) => {
				URL.revokeObjectURL(URL.createObjectURL(file))
			})

			const newFiles = maxFiles
				? [...selectedFiles, ...acceptedFiles].slice(0, maxFiles)
				: [...selectedFiles, ...acceptedFiles]
			setSelectedFiles(newFiles)
		},
		[selectedFiles, maxFiles],
	)

	const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
		const errorMessage = fileRejections
			.map((rejection) => rejection.errors.map((e: FileError) => e.message).join(', '))
			.join(', ')
		showErrorToast({
			message: errorMessage,
		})
	}, [])

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		onDropRejected,
		accept: {
			'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', ...additionalFilesTypes],
		},
		maxFiles: maxFiles ? maxFiles - selectedFiles.length : undefined,
	})

	useEffect(() => {
		if (externalOpen === true) {
			setOpen(true)
		}
	}, [externalOpen])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			selectedFiles.forEach((file) => {
				URL.revokeObjectURL(URL.createObjectURL(file))
			})
		}
	}, [selectedFiles])

	const handleOpen = () => setOpen(true)

	const handleClose = () => {
		// Clean up file URLs
		selectedFiles.forEach((file) => {
			URL.revokeObjectURL(URL.createObjectURL(file))
		})
		setOpen(false)
		setSelectedFiles([])
		setExternalOpen?.(false)
	}

	const handleRemoveFile = (index: number) => {
		const fileToRemove = selectedFiles[index]
		URL.revokeObjectURL(URL.createObjectURL(fileToRemove))

		const newFiles = selectedFiles.filter((_, i) => i !== index)
		setSelectedFiles(newFiles)
	}

	const handleSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
		if (selectedFiles.length === 0) {
			showErrorToast({ message: 'No images selected.' })
			return
		}
		setFiles(selectedFiles)
		handleClose()
	}

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
	}

	return (
		<>
			<Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<Box sx={{ mb: 2 }}>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
							Selected Images ({selectedFiles.length}
							{maxFiles ? `/${maxFiles}` : ''})
						</Typography>
						{selectedFiles.length > 0 && (
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
								{selectedFiles.map((file, index) => (
									<Chip
										key={index}
										label={`${file.name} (${formatFileSize(file.size)})`}
										onDelete={() => handleRemoveFile(index)}
										color='primary'
										variant='outlined'
										size='small'
									/>
								))}
							</Box>
						)}
					</Box>

					<div
						{...getRootProps()}
						style={{
							border: '2px dashed #ccc',
							padding: '20px',
							textAlign: 'center',
							borderRadius: '8px',
							backgroundColor: isDragActive ? '#f7f7f7' : '#fff',
							cursor: 'pointer',
							minHeight: '200px',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							transition: 'all 0.3s ease',
						}}
					>
						<input {...getInputProps()} />
						{isDragActive ? (
							<>
								<ImageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
								<Typography variant='h6' color='primary.main'>
									Drop images here...
								</Typography>
							</>
						) : selectedFiles.length > 0 ? (
							<>
								<ImageIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
								<Typography variant='h6' color='success.main'>
									Images selected!
								</Typography>
								<Typography variant='body2' color='text.secondary'>
									Drag & drop more images or click to select
								</Typography>
							</>
						) : (
							<>
								<ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
								<Typography variant='h6' color='text.secondary'>
									Drag & drop images here
								</Typography>
								<Typography variant='body2' color='text.secondary'>
									or click to select images
								</Typography>
								<Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
									Supported formats: JPEG, PNG, GIF, BMP, WebP,{' '}
									{additionalFilesTypes
										.map((type) => type.replace('.', '').toUpperCase())
										.join(', ')}
								</Typography>
							</>
						)}
					</div>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between' }}>
					<Button color='warning' variant='contained' onClick={handleClose}>
						Cancel
					</Button>
					<Button
						color='success'
						variant='contained'
						onClick={handleSubmit}
						disabled={selectedFiles.length === 0}
					>
						Submit ({selectedFiles.length})
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default ImageDropzone
